import {
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowDownload16Regular,
  ArrowUpload16Regular,
  Board16Regular,
  ChevronDown12Regular,
  Eye16Regular,
  EyeOff16Regular,
  TableSimple16Regular,
} from "@fluentui/react-icons";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog } from "@/components/app-dialog";
import { ListSearch, ListToolbar } from "@/components/list-toolbar";
import { usePageAction } from "@/shell/page-action";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import type { UserResource } from "@/engine/types";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { contentShell, shellGutter } from "@/shell/content-frame";
import { useCatalogStore, type ResourceWorkloadSummary } from "@/store/catalog-store";
import { ResourceFormDialog } from "@/features/resources/ResourceDialogs";
import { OrgExcelPreviewDialog } from "@/features/resources/OrgExcelPreviewDialog";
import { applyOrgImportPreview, previewOrgImport, type OrgImportPreview } from "@/features/resources/org-excel-preview";
import { exportOrgExcel, readOrgExcelFile } from "@/features/resources/org-excel-workbook";
import { ResourceBoard, boardAxisOf, moveResourceOnBoard } from "@/features/resources/ResourceBoard";
import { ResourceListTable, type ResourceListGroup } from "@/features/resources/ResourceListTable";
import { newId } from "@/store/tree-ops";
import { ResourcePoolDrawer } from "@/features/resources/ResourcePoolDrawer";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { EMPLOYMENT_LABEL, GRADE_LABEL } from "@/features/resources/resource-meta";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  nowrapBtn: {
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
  },
  listSlot: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  listFrame: {
    flex: 1,
    minHeight: 0,
  },
  empty: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalS,
  },
});

type PeopleGroup = ResourceListGroup;
type PeopleFilter = "all" | "available" | "over";
type ViewMode = "list" | "board";

export function GlobalResourcesPage() {
  const styles = useStyles();
  const navigate = useNavigate();

  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const projects = useCatalogStore((s) => s.projects);
  const addResource = useCatalogStore((s) => s.addResource);
  const updateResource = useCatalogStore((s) => s.updateResource);
  const deleteResource = useCatalogStore((s) => s.deleteResource);
  const applyOrgSnapshot = useCatalogStore((s) => s.applyOrgSnapshot);
  const getWorkload = useCatalogStore((s) => s.getResourceWorkload);
  const workspaceName = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]?.name ?? "워크스페이스");

  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [groupBy, setGroupBy] = useState<PeopleGroup>("none");
  const [filter, setFilter] = useState<PeopleFilter>("all");
  const [query, setQuery] = useState("");
  const [masked, setMasked] = useState(false);

  const [resourceDialog, setResourceDialog] = useState<{ open: boolean; initial: UserResource | null }>({ open: false, initial: null });
  usePageAction(null);
  const [deleteTarget, setDeleteTarget] = useState<UserResource | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const [applyingExcel, setApplyingExcel] = useState(false);
  const [excelPreview, setExcelPreview] = useState<OrgImportPreview | null>(null);
  const excelFileRef = useRef<HTMLInputElement>(null);

  const resList = useMemo(() => Object.values(resources), [resources]);
  const teamList = useMemo(() => Object.values(teams), [teams]);

  const workloads = useMemo(() => {
    const m: Record<string, ResourceWorkloadSummary> = {};
    for (const r of resList) m[r.id] = getWorkload(r.id);
    return m;
  }, [resList, projects, getWorkload]);

  const downloadOrgExcel = async (mode: "current" | "template") => {
    setExportingExcel(true);
    try {
      await exportOrgExcel({
        mode,
        workspaceName,
        teams: teamList,
        resources: resList,
        projects,
        workloads,
      });
    } finally {
      setExportingExcel(false);
    }
  };

  const onExcelSelected = async (file: File | undefined) => {
    if (!file) return;
    setImportingExcel(true);
    try {
      const parsed = await readOrgExcelFile(file);
      setExcelPreview(previewOrgImport(parsed, { teams, resources }));
    } catch {
      setExcelPreview({
        schemaVersion: null,
        fileName: file.name,
        blocking: true,
        issues: [
          {
            severity: "error",
            sheet: "파일",
            excelRow: 0,
            field: "xlsx",
            value: file.name,
            message: "엑셀 파일을 읽지 못했습니다. .xlsx 형식인지 확인해 주세요",
            action: "적용 불가",
          },
        ],
        teams: [],
        people: [],
        applyableCount: 0,
        errorCount: 1,
        warningCount: 0,
        createCount: 0,
        updateCount: 0,
      });
    } finally {
      setImportingExcel(false);
      if (excelFileRef.current) excelFileRef.current.value = "";
    }
  };

  const totalCount = resList.length;
  const avgAllocation = totalCount ? Math.round((Object.values(workloads).reduce((a, w) => a + w.totalAllocationRatio, 0) / totalCount) * 100) : 0;
  const description = `${totalCount}명 · 팀 ${teamList.length}개 · 평균 가동 ${avgAllocation}%`;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resList
      .filter((r) => {
        const wl = workloads[r.id];
        if (filter === "over" && !wl?.isOverAllocated) return false;
        if (filter === "available" && (wl?.totalAllocationRatio ?? 0) >= 0.95) return false;
        if (!q) return true;
        return [r.displayName, r.roleTitle, r.email, ...r.skills, teams[r.teamId ?? ""]?.name ?? ""].some((s) => s.toLowerCase().includes(q));
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName, "ko"));
  }, [resList, query, filter, workloads, teams]);

  /** Grouped card sections; "project" duplicates people across the plans they are on. */
  const groups = useMemo<Array<{ key: string; label: string; color?: string; icon?: string; hint?: string; items: UserResource[] }>>(() => {
    // 보드 뷰일 때는 'none' 상태여도 팀별 레인으로 기본 분할하여 칸반 구성
    const effectiveGroup = viewMode === "board" && groupBy === "none" ? "team" : groupBy;

    if (effectiveGroup === "none") return [{ key: "all", label: "", items: filtered }];
    const map = new Map<string, { label: string; color?: string; icon?: string; hint?: string; items: UserResource[] }>();
    const push = (key: string, label: string, r: UserResource, extra: { color?: string; icon?: string; hint?: string } = {}) => {
      const g = map.get(key) ?? { label, items: [], ...extra };
      if (!g.items.includes(r)) g.items.push(r);
      map.set(key, g);
    };

    // 팀 기준일 때는 인원이 없는 팀도 레인으로 보여주기 위해 미리 초기화
    if (effectiveGroup === "team") {
      for (const t of teamList) {
        map.set(t.id, { label: t.name, color: t.color, icon: t.icon, hint: t.description, items: [] });
      }
      map.set("__none", { label: "소속 없음", items: [] });
    }

    for (const r of filtered) {
      if (effectiveGroup === "team") {
        const t = r.teamId ? teams[r.teamId] : undefined;
        push(t?.id ?? "__none", t?.name ?? "소속 없음", r, { color: t?.color, icon: t?.icon, hint: t?.description });
      } else if (effectiveGroup === "employment") {
        push(r.employmentType, EMPLOYMENT_LABEL[r.employmentType], r);
      } else if (effectiveGroup === "grade") {
        push(r.grade, GRADE_LABEL[r.grade], r);
      } else {
        const wl = workloads[r.id];
        if (!wl || wl.assignments.length === 0) push("__idle", "투입 대기", r);
        for (const a of wl?.assignments ?? []) push(a.projectId, a.projectName, r);
      }
    }

    const arr = [...map.entries()].map(([key, g]) => ({ key, ...g }));
    if (effectiveGroup === "team") {
      return arr; // 팀 순서 유지
    }
    return arr.sort((a, b) => b.items.length - a.items.length);
  }, [filtered, groupBy, viewMode, teamList, teams, workloads]);

  const boardAxis = boardAxisOf(groupBy);
  const handleBoardMove = (resourceId: string, groupKey: string) => {
    moveResourceOnBoard(boardAxis, resourceId, groupKey, updateResource);
  };
  const filteredEmpty = filtered.length === 0;

  return (
    <div className={styles.page}>
      <ListToolbar
        title="리소스"
        description={description}
        headingAction={
          <FuiButton appearance="subtle" className={styles.nowrapBtn} icon={masked ? <EyeOff16Regular /> : <Eye16Regular />} aria-pressed={masked} onClick={() => setMasked(!masked)}>
            {masked ? "단가 비공개" : "단가 공개"}
          </FuiButton>
        }
        search={<ListSearch value={query} onChange={setQuery} />}
        filters={
          <>
            <SegmentedControl
              ariaLabel="인력 범위"
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: "전체" },
                { value: "available", label: "투입 가능" },
                { value: "over", label: "과부하" },
              ]}
            />
            <FuiFilterSelect
              label="묶기"
              ariaLabel="묶기"
              value={viewMode === "board" && groupBy === "none" ? "team" : groupBy}
              options={viewMode === "board"
                ? [
                    { value: "team", label: "팀" },
                    { value: "project", label: "플랜" },
                    { value: "employment", label: "고용형태" },
                    { value: "grade", label: "등급" },
                  ]
                : [
                    { value: "none", label: "없음" },
                    { value: "team", label: "팀" },
                    { value: "project", label: "플랜" },
                    { value: "employment", label: "고용형태" },
                    { value: "grade", label: "등급" },
                  ]}
              onValueChange={(v) => setGroupBy(v as PeopleGroup)}
            />
          </>
        }
        trail={
          <>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <FuiButton className={styles.nowrapBtn} appearance="subtle" disabled={exportingExcel || importingExcel}>
                  {exportingExcel ? "생성 중" : importingExcel ? "읽는 중" : "Excel"}
                  <ChevronDown12Regular />
                </FuiButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem icon={<ArrowDownload16Regular />} onClick={() => void downloadOrgExcel("current")}>
                    현재 데이터 다운로드
                  </MenuItem>
                  <MenuItem icon={<ArrowDownload16Regular />} onClick={() => void downloadOrgExcel("template")}>
                    빈 양식 다운로드
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem icon={<ArrowUpload16Regular />} onClick={() => excelFileRef.current?.click()}>
                    업로드
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            <input
              ref={excelFileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => void onExcelSelected(e.target.files?.[0])}
            />
          </>
        }
        view={
          <SegmentedControl
            value={viewMode}
            onChange={(v) => setViewMode(v as ViewMode)}
            ariaLabel="뷰 형태 선택"
            options={[
              { value: "list", icon: <TableSimple16Regular />, label: "목록" },
              { value: "board", icon: <Board16Regular />, label: "보드" },
            ]}
          />
        }
      />

      <div className={styles.body}>
        {filteredEmpty ? (
          <div className={styles.empty}>
            <GhostEmptyState
              variant={query || filter !== "all" ? "search" : "default"}
              title={query || filter !== "all" ? "조건에 맞는 인력이 없습니다" : "등록된 인력이 없습니다"}
              description={query || filter !== "all" ? "검색어나 필터를 바꿔 보세요." : "엑셀 업로드로 조직 풀을 채울 수 있습니다."}
            />
            {(query || filter !== "all") && (
              <FuiButton
                appearance="subtle"
                onClick={() => {
                  setQuery("");
                  setFilter("all");
                }}
              >
                필터 지우기
              </FuiButton>
            )}
          </div>
        ) : viewMode === "list" ? (
          <div className={styles.listSlot}>
            <ResourceListTable
              className={styles.listFrame}
              resources={filtered}
              teams={teams}
              teamOrder={teamList}
              workloads={workloads}
              groupBy={groupBy}
              masked={masked}
              onOpen={(resource) => setProfileId(resource.id)}
              onEdit={(resource) => setResourceDialog({ open: true, initial: resource })}
              onDelete={setDeleteTarget}
              onOpenProject={(projectId) => navigate(`/projects/${projectId}/team`)}
            />
          </div>
        ) : (
          <ResourceBoard
            groups={groups}
            axis={boardAxis}
            teams={teams}
            workloads={workloads}
            masked={masked}
            onMove={handleBoardMove}
            onOpen={(resource) => setProfileId(resource.id)}
            onEdit={(resource) => setResourceDialog({ open: true, initial: resource })}
            onDelete={setDeleteTarget}
            onOpenProject={(projectId) => navigate(`/projects/${projectId}/team`)}
          />
        )}

      </div>

      {/* dialogs & drawers */}
      {resourceDialog.open && (
        <ResourceFormDialog
          open
          initial={resourceDialog.initial}
          teams={teamList}
          onClose={() => setResourceDialog({ open: false, initial: null })}
          onSave={(data) => {
            if (resourceDialog.initial) updateResource(resourceDialog.initial.id, data);
            else addResource(data);
            setResourceDialog({ open: false, initial: null });
          }}
        />
      )}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="인력 삭제"
        message={
          deleteTarget ? (
            <>
              이 인력을 전사 인력풀에서 삭제할까요?
              {workloads[deleteTarget.id]?.projectCount ? ` 현재 ${workloads[deleteTarget.id]!.projectCount}개 플랜에 배정되어 있습니다.` : ""}
            </>
          ) : null
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteResource(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
      <ResourcePoolDrawer open={Boolean(profileId)} onClose={() => setProfileId(null)} initialResourceId={profileId} masked={masked} />
      <OrgExcelPreviewDialog
        open={Boolean(excelPreview)}
        preview={excelPreview}
        applying={applyingExcel}
        onClose={() => {
          if (applyingExcel) return;
          setExcelPreview(null);
        }}
        onApply={() => {
          if (!excelPreview || excelPreview.blocking || excelPreview.applyableCount === 0) return;
          setApplyingExcel(true);
          try {
            const next = applyOrgImportPreview(excelPreview, { teams, resources }, newId);
            applyOrgSnapshot(next.teams, next.resources);
            setExcelPreview(null);
          } finally {
            setApplyingExcel(false);
          }
        }}
      />
    </div>
  );
}
